import { BootState } from '@/boot'
import axios, { AxiosResponse } from 'axios'

/** Supported file types for Settings Logo upload */
const allowedLogoFileTypes = ['image/png', 'image/jpeg', 'image/svg+xml'] as const
/** Type of each possible value for `allowedLogoFileTypes` */
type AllowedLogoFileType = typeof allowedLogoFileTypes[number]
/** Type-guard function to check if a given string is of type `AllowedLogoFileType` */
function isAllowedLogoFileType(x: string): x is AllowedLogoFileType {
  return allowedLogoFileTypes.includes(x as AllowedLogoFileType)
}

export type UiColorSettingName = 'primaryColor' | 'backgroundFontColor'
export type UiSettingName = UiColorSettingName | 'clientLogo'

const apiV1SettingUrlBase = '/api/v1/core/settings/launcher'

export function standardizeHexColorCasing(hexColor: string) {
  return hexColor.toLowerCase()
}

export class UISettings {
  /** Holds the prior values of settings so that things can be rolled back */
  public priorUiOptions: Record<UiSettingName, string> = {
    primaryColor: '',
    backgroundFontColor: '',
    clientLogo: '',
  }

  /**
   * Creates the instance of uiSettings
   * @param bootState
   */
  public constructor(public bootState: BootState) {}

  /** Stage change for given setting name with given new value */
  public stageChange(key: UiSettingName, value: string): void {
    // If value is identical to current value then no-op
    if (this.bootState.tedsUi.options[key] === value) {
      return
    }
    // If value is identical to prior value then rollback
    else if (this.priorUiOptions[key] === value) {
      return this.rollbackStagedChange(key)
    }
    // If there was no prior value staged then set prior value
    else if (!this.isChangeStagedFor(key)) {
      this.priorUiOptions[key] = this.bootState.tedsUi.options[key]
      this.bootState.tedsUi.options[key] = value
    }
    // Else there was already a prior value then overwrite staged value
    else {
      this.bootState.tedsUi.options[key] = value
    }
  }

  /** Returns whether there is a staged change for given setting name */
  public isChangeStagedFor(key: UiSettingName): boolean {
    return this.priorUiOptions[key] !== ''
  }

  /** Rollback the staged change or throw and error if unable */
  public rollbackStagedChange(key: UiSettingName, errorIfUnable = true): void {
    if (this.isChangeStagedFor(key)) {
      this.bootState.tedsUi.options[key] = this.priorUiOptions[key]
      this.priorUiOptions[key] = ''
    } else if (errorIfUnable) {
      throw new Error(`No value to roll back to for setting ${key}`)
    }
  }

  /** Commit the staged change or error if there is none */
  public commitStagedChange(key: UiSettingName): void {
    if (this.isChangeStagedFor(key)) {
      this.priorUiOptions[key] = ''
    } else {
      throw new Error('No change staged to commit')
    }
  }

  /** Helper function to idempotent-ly rollback any staged color changes */
  public rollbackStagedColorChangesIfAny(): void {
    this.rollbackStagedChange('primaryColor', false)
    this.rollbackStagedChange('backgroundFontColor', false)
  }

  /** Helper function to check if there are any staged color changes */
  public isChangeStagedForAnyColor() {
    return this.isChangeStagedFor('primaryColor') || this.isChangeStagedFor('backgroundFontColor')
  }

  /** Update given color on server with the staged value or error if there isn't a staged value */
  public async updateColor(key: UiColorSettingName): Promise<AxiosResponse<unknown>> {
    if (!this.isChangeStagedFor(key)) {
      throw new Error(`No staged changed value for ${key}`)
    }
    const urlSettingName = {
      primaryColor: 'ui_primary_color',
      backgroundFontColor: 'ui_background_font_color',
    }[key]

    try {
      const response = await this.bootState.request.put(`${apiV1SettingUrlBase}/${urlSettingName}`, {
        value: this.bootState.tedsUi.options[key],
      })
      this.commitStagedChange(key)
      return response
    } catch (err) {
      this.rollbackStagedChange(key)
      throw this.printAndThrowError(`Failed to save change for ${key}`, err)
    }
  }

  /** Reads given file and returns "dataUrl" version of it as a sting. */
  public async readBase64DataUrlOfFile(file: File): Promise<string> {
    const fileReaderPromise = new Promise<FileReader>(done => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (): void => done(reader)
    })
    const result = (await fileReaderPromise).result
    if (typeof result !== 'string') {
      throw new Error('File reader produced non-string result')
    }
    return result
  }

  /** Update logo on server with the staged value or error if there isn't a staged value */
  public async updateLogo(): Promise<AxiosResponse<unknown>> {
    if (!this.isChangeStagedFor('clientLogo')) {
      throw new Error('No staged changed value for clientLogo')
    }
    try {
      const response = await this.bootState.request.put<unknown>(
        `/api/v1/core/settings/launcher/ui_logo_base64`,
        // Payload
        { value: this.bootState.tedsUi.options.clientLogo },
        // Request config, 10 second timeout
        { timeout: 10_000 }
      )
      this.commitStagedChange('clientLogo')
      return response
    } catch (err) {
      this.rollbackStagedChange('clientLogo')
      let errMsg
      if (axios.isAxiosError(err)) {
        errMsg = `Could not update logo (${err.message})`
      } else {
        errMsg = `Unexpected error when updating logo, please notify system admin.`
      }
      throw this.printAndThrowError(errMsg, err)
    }
  }

  /**
   * What happens when a file is selected as the new logo
   * @param fileInput
   * @param event
   */
  public async logoSelected(files: FileList | null): Promise<void> {
    // If no file selected then error
    if (!files || !files[0]) {
      return this.printAndThrowError('No image selection found for logo')
    }
    // Grab the first file
    const file = files[0]
    // Make sure that the logo is an allowed file type
    if (!isAllowedLogoFileType(file.type)) {
      const allowedFileTypesString = allowedLogoFileTypes.join(', ')
      return this.printAndThrowError(`You can not use this file. Allowed file types are: ${allowedFileTypesString}`)
    }
    // Validate file size
    if (file.size > 1_400_000) {
      return this.printAndThrowError('File too large, max file size is 1.4MB')
    }
    // Attempt to read file
    let logoAsBase64DataUrl
    try {
      logoAsBase64DataUrl = await this.readBase64DataUrlOfFile(file)
    } catch (err) {
      return this.printAndThrowError('Cannot process file', err)
    }
    // Validate payload size
    if (logoAsBase64DataUrl.length > 2_000_000) {
      return this.printAndThrowError('Internal error: File payload too large, max size is 2MB')
    }
    // Change state to new value if all above checks pass
    this.stageChange('clientLogo', logoAsBase64DataUrl)
  }

  /**
   * Takes `msg` and console log plus notification prints it, throws
   * given error or new one from `msg`
   */
  private printAndThrowError(msg: string, error?: Error | unknown) {
    console.error(msg)
    if (error) {
      console.error(error)
    }
    this.bootState.tedsUi.notifications.error(msg)
    if (error) {
      throw error
    } else {
      throw new Error(msg)
    }
  }
}
